<template>
  <div>
    <div>
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between">
          <span>Machine Base File</span>
          <div v-if="userHasRole">
            <div class="btn-group">
              <b-button @click="navigateToEditMachineFile" size="sm" variant="outline-dark">
                <font-awesome-icon icon="edit"/>
                Edit
              </b-button>
              <b-button v-b-modal="'delete-machine'" size="sm" variant="outline-dark"
                        v-if="$store.getters.canDeleteMachine" :disabled="machine.items.length > 0"
                        title="A machine can only be deleted if all it's life cycle entries are deleted or moved to another machine.">
                <font-awesome-icon icon="trash-can"/>
                Delete
              </b-button>
            </div>
          </div>
        </div>
        <ul class="list-group list-group-flush">
          <li class="list-group-item">
            <div class="float-left">
              <font-awesome-icon icon="barcode"/>
              S/N
            </div>
            <div class="float-right"><span>{{ machine.basefile.serial }}</span></div>
          </li>
          <li class="list-group-item">
            <div class="float-left">
              <font-awesome-icon icon="tags"/>
              Type
            </div>
            <div class="float-right"><span>{{ machine.basefile.type }}</span></div>
          </li>
          <li class="list-group-item">
            <div class="float-left">
              <font-awesome-icon icon="calendar-alt"/>
              CWR Date
            </div>
            <div class="float-right">
              <div v-if="machine.latest.cwr">
                <span>{{ machine.latest.cwr.timestamp }}</span>
              </div>
              <span v-else class="text-muted">(no data)</span>
            </div>
          </li>
          <li class="list-group-item">
            <div class="float-left">
              <font-awesome-icon icon="briefcase"/>
              Owner
            </div>
            <div class="float-right"><span>{{ machine.basefile.owner }}</span></div>
          </li>
          <li v-if="machine.basefile.coverimage" class="list-group-item">
            <font-awesome-icon icon="camera"/>
            Picture<br>
            <img :src="'/frontend-api/report/' + machine.basefile.coverimage"
                 alt="Cover image" class="mt-2" style="width: 100%; height: auto; border: 1px solid black;">
          </li>
        </ul>
        <div class="card-footer">
          <div class="row">
            <div class="col-sm">
              <router-link :to="'/machine/' + machine.basefile.serial + '/details'">
                <font-awesome-icon icon="weight"/>
                Details page
              </router-link>
            </div>
            <div class="col-sm text-right" v-if="openReviews">
              <open-reviews-notification :machineSerial="machine.basefile.serial" identifier="machineInfoNotifications"/>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="card-header">
          Key Documents
        </div>
        <ul class="list-group list-group-flush">
          <li class="list-group-item">
            <div class="float-left">
              <font-awesome-icon icon="file"/>
              Manual
            </div>
            <div class="float-right">
              <div v-if="machine.latest.manual">
                <a :href="'/frontend-api/entry/' + machine.latest.manual.longid + '/' + machine.latest.manual.filename">{{
                    machine.latest.manual.filename
                  }}</a>
              </div>
              <span v-else class="text-muted">(no data)</span>
            </div>
          </li>
          <li class="list-group-item">
            <div class="float-left">
              <font-awesome-icon icon="list-alt"/>
              Assembly Log
            </div>
            <div class="float-right">
              <div v-if="machine.latest.assemblylog">
                <a :href="'/frontend-api/entry/' + machine.latest.assemblylog.longid + '/' + machine.latest.assemblylog.filename">{{
                    machine.latest.assemblylog.filename
                  }}</a>
              </div>
              <span v-else class="text-muted">(no data)</span>
            </div>
          </li>
          <li class="list-group-item">
            <div class="float-left">
              <font-awesome-icon icon="weight"/>
              CWR
            </div>
            <div class="float-right">
              <div v-if="machine.latest.cwr">
                <a :href="'/frontend-api/entry/' + machine.latest.cwr.longid + '/' + machine.latest.cwr.filename">{{
                    machine.latest.cwr.filename
                  }}</a>
              </div>
              <span v-else class="text-muted">(no data)</span>
            </div>
          </li>
          <li class="list-group-item">
            <div class="float-left">
              <font-awesome-icon icon="weight"/>
              Latest Report
            </div>
            <div class="float-right">
              <div v-if="machine.latest.report">
                <router-link :to="'/report/' + machine.latest.report.longid">
                  {{ machine.latest.report.filename ? machine.latest.report.filename : 'Unlabeled' }}
                </router-link>
              </div>
              <span v-else class="text-muted">(no data)</span>
            </div>
          </li>
        </ul>
      </div>
      <div id="documentArchive" class="card">
        <div class="card-header">
          Document Archive
        </div>
        <ul class="list-group list-group-flush">
          <li class="list-group-item">
            <a v-b-toggle.collapse-reports style="cursor: pointer">
              <font-awesome-icon class="auto" icon="folder-open"/>
              <font-awesome-icon class="auto" icon="folder"/>
              ScreenCheck Reports
            </a>
            <b-collapse id="collapse-reports" visible>
              <ul>
                <li v-for="report in machine.docsReports" :key="report.longid">
                  <router-link :to="'/report/' + report.longid">
                    <font-awesome-icon class="mr-2" icon="file"/>
                    <span v-if="report.filename">
                      {{ report.filename }}
                    </span>
                    <span v-else>
                      Unlabeled
                    </span>
                  </router-link>
                </li>
              </ul>
            </b-collapse>
          </li>
          <li v-if="isUserAdmin" class="list-group-item">
            <a v-b-toggle.collapse-hidden-reports style="cursor: pointer">
              <font-awesome-icon class="auto" icon="folder-open"/>
              <font-awesome-icon class="auto" icon="folder"/>
              Hidden ScreenCheck Reports
            </a>
            <b-collapse id="collapse-hidden-reports">
              <ul>
                <li v-for="report in machine.docsReportsHidden" :key="report.longid">
                  <router-link :to="'/report/' + report.longid">
                    <font-awesome-icon class="mr-2" icon="file"/>
                    <span v-if="report.filename">
                      {{ report.filename }}
                    </span>
                    <span v-else>
                      Unlabeled
                    </span>
                  </router-link>
                </li>
              </ul>
            </b-collapse>
          </li>
          <li class="list-group-item">
            <a v-b-toggle.collapse-attachments style="cursor: pointer">
              <font-awesome-icon class="auto" icon="folder-open"/>
              <font-awesome-icon class="auto" icon="folder"/>
              Uploaded Attachments
            </a>
            <b-collapse id="collapse-attachments">
              <ul>
                <li v-for="(item, index) in attachments" :key="index">
                  <a :href="'/frontend-api/entry/' + item.entry.longid + '/' + item.entry.filename">
                    <font-awesome-icon class="mr-2" icon="file"/>
                    {{ item.entry.filename }}
                  </a>
                </li>
              </ul>
            </b-collapse>
          </li>
        </ul>
      </div>
    </div>
    <!-- delete machine Modal -->
    <b-modal id="delete-machine" ref="delete-machine" :title="'Deleting Screen: ' + machine.basefile.serial" @ok="deleteMachine($event)"
             ok-title="Delete" ok-variant="danger" @hide="deleteMachineError = null">
      <b-alert :show="deleteMachineError !== null" variant="danger">
        {{ deleteMachineError }}
      </b-alert>
      <ul class="list-group list-group-flush">
        <li class="list-group-item">
          <div class="float-left">
            <font-awesome-icon icon="barcode"/>
            S/N
          </div>
          <div class="float-right"><span>{{ machine.basefile.serial }}</span></div>
        </li>
        <li class="list-group-item">
          <div class="float-left">
            <font-awesome-icon icon="tags"/>
            Type
          </div>
          <div class="float-right"><span>{{ machine.basefile.type }}</span></div>
        </li>
      </ul>
    </b-modal>
  </div>
</template>

<script>

import { DetailedMachineInformation } from '@/screencheck_portal_api/src'
import { machinesApi } from '@/backend'
import OpenReviewsNotification from '@/components/machine_file/OpenReviewsNotification.vue'

export default {
  name: 'MachineInfo',
  props: {
    machine: {
      type: DetailedMachineInformation
    }
  },
  components: { OpenReviewsNotification },
  data () {
    return {
      modalMachine: {
        owner: '',
        type: '',
        serial: ''
      },
      deleteMachineError: null
    }
  },
  mounted () {
    this.resetModalData()
  },
  computed: {
    attachments: function () {
      return this.machine.items.filter(item => item.isEntry && item.entry.filename)
    },
    isUserAdmin: function () {
      return this.$store.getters.isUserAdmin
    },
    userHasRole: function () {
      return this.isUserAdmin || this.$store.getters.isUserEngineeringManager
    },
    openReviews: function () {
      return this.machine.openReviews.count > 0
    },
  },
  methods: {
    deleteMachine: function (ev) {
      ev.preventDefault()
      machinesApi.deleteMachine(this.machine.basefile.serial, (error) => {
        if (error) {
          console.error('Failed deleting machine. ' + error.response.body.message)
          this.deleteMachineError = 'Failed deleting machine. ' + error.response.body.message
          return
        }
        this.deleteMachineError = null
        this.$refs['delete-machine'].hide()
        this.$router.push({ name: 'machines' }).then(() => {
          window.location.reload()
        })
      })
    },
    resetModalData: function () {
      this.modalMachine.owner = this.machine.basefile.owner
      this.modalMachine.type = this.machine.basefile.type
      this.modalMachine.serial = this.machine.basefile.serial
    },
    navigateToEditMachineFile: function () {
      this.$router.push('/machine/' + this.machine.basefile.serial + '/edit')
    }
  }
}
</script>

<style lang="scss">
</style>
